import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BfxBmarkSummarySettingsComponent } from "app/bfx-user-settings/bfx-bmark-summary-settings.component";
import {
  BfxUserSettingsValidityState,
  IBfxUserSettingsPageSubComponent,
} from "app/bfx-user-settings/bfx-user-settings-page.component";
import { BfxUserSettingsComponent } from "app/bfx-user-settings/bfx-user-settings.component";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ProfileUpdateRequestImpl } from "shared/cubiq-model-multi-asset";
import {
  ActiveUser,
  ProfilePromiseActionService,
  ProfileSettingKeys,
  TimezonePromiseLookupService,
  UserPromiseLookupService,
} from "shared/cubiq-services";
import { BfxUserSettingsComponentState, BfxUserSettingsService } from "./bfx-user-settings.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

/** TO be changed - this is not not part of BM Summary settings */
export const SETTINGS_PROPERTY_AMOUNTVIEWTYPE = "amountViewType";
export const PROFILE_SETTINGS_EXTCOLOUR_HIGHLIGHTING = "externalColoursModel";

type TzType = {
  id: string;
  idlc: string;
  label: string;
};
const LOCAL_TIME_TZ: TzType = { id: "", label: "Local Time", idlc: "" };

export type BMSummarySavedSettingType = {
  fixingTimeTz: string;
  externalColoursModel: "y" | "n";
};

/**
 * Component that manages the UI settings for the Bmark Summary Preferences tab
 */
@Component({
  selector: "bfx-bmark-summary-settings-preferences",
  templateUrl: "./html/bfx-bmark-summary-settings-preferences.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxBmarkSummarySettingsPeferencesComponent
  implements IBfxUserSettingsPageSubComponent<BfxBmarkSummarySettingsComponent>, OnInit, OnDestroy
{
  public activeSubComponent: number = 2;
  public categoryComponent: BfxBmarkSummarySettingsComponent = null;
  public parentComponent: BfxUserSettingsComponent = null;
  private tempValueToUpdateActiveUser: string;
  public ready: boolean = false;
  public externalColours: boolean;

  private timezones: TzType[];
  public timezoneSuggestions: TzType[];

  public timezoneId: TzType;

  constructor(
    public userSettingsService: BfxUserSettingsService,
    public profileService: ProfilePromiseActionService,
    public userService: UserPromiseLookupService,
    private timezoneLookupService: TimezonePromiseLookupService,
    public element: ElementRef,
    public translationService: TranslateService,
    private cd: ChangeDetectorRef,
  ) {}

  public onTabActivated(event: any): void {}

  enableExternalColoursToggle($event: any) {
    this.externalColours = !this.externalColours;
    this.parentComponent.validate(null);
    this.cd.markForCheck();
  }

  /**
   * Called after parent component is fully initialised
   */
  public initialize(): void {
    this.timezoneLookupService.getAllTimezones().then((timezones: CubiqTS.TimeZoneImpl[]) => {
      this.timezones = timezones
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((tz) => {
          const label = `${tz.id} - ${tz.tzRegionName}`;
          return {
            id: tz.id,
            label,
            idlc: label.toLowerCase(),
          };
        });
      this.timezones.unshift(LOCAL_TIME_TZ);

      let activeUser: ActiveUser = this.parentComponent.activeUser;
      this.primeFromProfile(activeUser);
      // Bah, have to keep this set timeout to prevent user from e.g. toggling control which will enable
      // save, than can then be disabled from other initialising views in validate(....,true, true)
      setTimeout(() => {
        this.ready = true;
        this.cd.markForCheck();
      }, 100);
    });
  }

  private primeFromProfile(activeUser: ActiveUser) {
    if (activeUser.profileSettings) {
      const key = ProfileSettingKeys.userProfileSettingsBmarkSummaryPreferences;
      const profileValue = activeUser.profileSettings.get(key);
      let savedSettings: BMSummarySavedSettingType;
      try {
        if (profileValue) {
          savedSettings = JSON.parse(profileValue);
        }
      } catch (e) {
        logger.out.warn(`Error parsing json: ${profileValue} - ${e}`);
      }

      this.externalColours = savedSettings && savedSettings.externalColoursModel === "y";
      const tzId = savedSettings ? savedSettings.fixingTimeTz : "";
      this.timezoneSuggestions = this.timezones.concat();
      this.timezoneId = this.timezones.find((tz) => tz.id === tzId);
      if (this.timezoneId == null) {
        this.timezoneId = LOCAL_TIME_TZ;
      }
    } else {
      this.externalColours = false;
      this.timezoneId = LOCAL_TIME_TZ;
    }
  }

  ngOnInit() {}

  ngOnDestroy() {}

  public validate(): Promise<BfxUserSettingsValidityState> {
    return new Promise<BfxUserSettingsValidityState>((res, reject) => {
      let state: BfxUserSettingsValidityState;
      if (this.timezoneId && this.timezoneId.hasOwnProperty("label")) {
        state = BfxUserSettingsValidityState.VALID;
      } else {
        state = BfxUserSettingsValidityState.INVALID;
      }
      res(state);
    });
  }

  public reset(): Promise<void> {
    return new Promise<void>((res, reject) => {
      let activeUser: ActiveUser = this.parentComponent.activeUser;
      this.primeFromProfile(activeUser);
      this.cd.markForCheck();
      res();
    });
  }

  public save(request: ProfileUpdateRequestImpl): void {
    const key = ProfileSettingKeys.userProfileSettingsBmarkSummaryPreferences;
    let settingObject = request.settings.find((setting) => setting.profileSettingKey === key);
    if (settingObject == null) {
      settingObject = new CubiqTS.ProfileSettingImpl();
      settingObject.profileSettingKey = key;
      request.settings.push(settingObject);
    }

    let objToSave: BMSummarySavedSettingType = {
      externalColoursModel: this.externalColours ? "y" : "n",
      fixingTimeTz: this.timezoneId?.id ?? "",
    };
    this.tempValueToUpdateActiveUser = JSON.stringify(objToSave);
    settingObject.profileSettingValue = this.tempValueToUpdateActiveUser;
  }

  public postReset(): void {}

  public postSave(request: ProfileUpdateRequestImpl): void {
    let activeUser: ActiveUser = this.parentComponent.activeUser;
    if (activeUser.profileSettings) {
      activeUser.profileSettings.set(
        ProfileSettingKeys.userProfileSettingsBmarkSummaryPreferences,
        this.tempValueToUpdateActiveUser,
      );
    }
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {}

  public hasDataChanged(): boolean {
    return null;
  }

  public searchTimezones(event) {
    const searchTerm = event.query.toLowerCase();
    this.timezoneSuggestions = this.timezones.filter((tz) => tz.idlc.indexOf(searchTerm) != -1);
    this.cd.markForCheck();
  }

  public onBlurTz() {
    if (this.timezoneId == null || <any>this.timezoneId === "") {
      setTimeout(() => {
        this.timezoneSuggestions = this.timezones.concat();
        this.timezoneId = this.timezones[0];
        this.cd.markForCheck();
      }, 100);
    }
  }
  public onSelectTz() {
    // logger.out.info("TZ Selected");
    this.parentComponent.validate(null);
  }
}
